<script lang="ts" setup>
// Figma name: ToolTip
// https://www.figma.com/file/1HfA941cU4A9RZxXHLmbpG/AGIDB---Design-System?type=design&node-id=199%3A5717&mode=dev

import {
  type AutoPlacementOptions,
  type FlipOptions,
  type OffsetOptions,
  type Placement,
} from '@floating-ui/vue'
import { computed, nextTick, ref, useId, watch } from 'vue'

import { useTooltip } from '@/sharedComposables/useTooltip'
import DarwinButton from '@/uiKit/DarwinButton.vue'
import IconSprite from '@/uiKit/IconSprite.vue'
import ToolTipContent from '@/uiKit/ToolTipContent.vue'
import type { IconName } from './IconName'
import PopOver from './PopOver.vue'

export type ToolTipProps = {
  title?: string
  body?: string
  buttonIcon?: IconName
  buttonLabel?: string
  arrow?: boolean
  offset?: number | OffsetOptions
  /**
   * High level rules for how the popup should be placed.
   *
   * @see https://floating-ui.com/docs/autoPlacement
   */
  autoPlacementOptions?: AutoPlacementOptions
  flipOptions?: FlipOptions
  placement?: Placement
  disabled?: boolean
  disableTeleport?: boolean
  ariaLabelledby?: string
  targetSelector?: string
  /** Delay in ms before the tooltip appears. */
  appearDelay?: number
}

const props = defineProps<ToolTipProps>()

const emit = defineEmits<{
  (e: 'action' | 'close'): void
  (e: 'change:open', open: boolean): void
}>()

const { isOpen, popover, setClosed, trigger } = useTooltip({
  disabled: computed(() => props.disabled),
  appearDelay: props.appearDelay,
})

const role = computed(() => (props.buttonLabel ? 'dialog' : 'tooltip'))

const onToggle = (isOpen: boolean) => {
  if (!isOpen) {
    setClosed()
    return
  }
}

const externalTarget = ref<HTMLElement | null>(null)
watch(
  () => props.targetSelector,
  async () => {
    await nextTick()
    if (props.targetSelector) {
      externalTarget.value = document.querySelector<HTMLElement>(props.targetSelector) || null
    } else {
      externalTarget.value = null
    }
  },
  { immediate: true },
)

let externalTargetController: AbortController | null = null
watch(
  externalTarget,
  (newTarget, oldTarget) => {
    // target has changed to a new target, not to null
    if (newTarget !== oldTarget) {
      externalTargetController?.abort()

      // now new target, so just abort and return
      if (!newTarget || !newTarget.closest('body')) {
        externalTargetController = null
        return
      }

      // new target, so set up new controller and events
      externalTargetController = new AbortController()

      newTarget.addEventListener(
        'mouseover',
        () => newTarget.closest('body') && trigger.onMouseOver(),
        {
          signal: externalTargetController.signal,
        },
      )
      newTarget.addEventListener(
        'mouseleave',
        () => newTarget.closest('body') && trigger.onMouseLeave(),
        {
          signal: externalTargetController.signal,
        },
      )
      newTarget.addEventListener(
        'focusin',
        () => newTarget.closest('body') && trigger.onFocusIn(),
        {
          signal: externalTargetController.signal,
        },
      )
      newTarget.addEventListener(
        'focusout',
        () => newTarget.closest('body') && trigger.onFocusOut(),
        {
          signal: externalTargetController.signal,
        },
      )
      newTarget.addEventListener('click', () => newTarget.closest('body') && trigger.onClick(), {
        signal: externalTargetController.signal,
      })
    }
  },
  { immediate: true },
)

watch(isOpen, (newOpen) => {
  emit('change:open', newOpen)
})
const popoverId = useId()
</script>

<template>
  <PopOver
    :open="isOpen"
    :arrow="arrow"
    :auto-placement-options="autoPlacementOptions"
    :flip-options="flipOptions"
    :placement="placement"
    :role="role"
    :aria-labelledby="ariaLabelledby"
    :offset="offset"
    :disable-teleport="disableTeleport"
    :target-selector="externalTarget"
    @mouseover:popover="popover.onMouseOver"
    @toggle:popover="onToggle"
    @afterclose="emit('close')"
  >
    <template
      v-if="$slots.default"
      #trigger
    >
      <span
        data-test="tooltip-target"
        class="contents"
        @mouseover="trigger.onMouseOver"
        @mouseleave="trigger.onMouseLeave"
        @focusin="trigger.onFocusIn"
        @focusout="trigger.onFocusOut"
        @click="trigger.onClick"
      >
        <slot
          :is-open="isOpen"
          :aria-describedby="popoverId"
        />
      </span>
    </template>

    <template #content>
      <div
        :id="popoverId"
        class="relative w-max max-w-[352px] overflow-hidden rounded-corner-6 border border-border"
        @mouseover="popover.onMouseOver"
        @mouseleave="popover.onMouseLeave"
      >
        <div class="bg-surface-popover-inverted text-text-inverted-irreversible">
          <slot name="content">
            <ToolTipContent>
              <template
                v-if="title"
                #title
              >
                {{ title }}
              </template>
              <template
                v-if="body"
                #body
              >
                {{ body }}
              </template>
              <template
                v-if="buttonLabel"
                #button
              >
                <DarwinButton
                  v-if="buttonLabel"
                  size="xs"
                  variant="blue"
                  class="w-fit text-text"
                  @click="$emit('action')"
                >
                  <template
                    v-if="buttonIcon"
                    #leading-icon
                  >
                    <IconSprite :icon="buttonIcon" />
                  </template>
                  {{ buttonLabel }}
                </DarwinButton>
              </template>
            </ToolTipContent>
          </slot>
        </div>
      </div>
    </template>
  </PopOver>
</template>
